import { Badge } from '@/components/ui/badge';
import { buildCsv } from '@/lib/csv';
import { downloadText } from '@/lib/download';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import {
    FormSection,
    MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
    FilterConfig,
    MasterDataTable,
    TableColumn,
} from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { FreeFormNote } from '../types/freeFormNote';

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const FreeFormNoteManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingFreeFormNote, setEditingFreeFormNote] = useState<FreeFormNote | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>(
    {}
  );
  const [sortConfig, setSortConfig] = useState<{
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  // Fetch free form notes with server-side filtering and pagination
  const {
    data: freeFormNotesResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'freeFormNotes',
      currentPage,
      pageSize,
      searchTerm,
      currentFilters,
      sortConfig,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(
        `/api/master-data/freeFormNote?${params.toString()}`
      );
      const result = response.data.data || {};

      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const freeFormNotes = freeFormNotesResponse?.data || [];
  const pagination = freeFormNotesResponse?.pagination;

  // Create mutation
  const createFreeFormNoteMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/freeFormNote', data);
      return response.data;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['freeFormNotes'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'freeFormNote'],
      });
      toast({
        title: 'Success',
        description: 'Free Form Note created successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to create free form note';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateFreeFormNoteMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(
        `/api/master-data/freeFormNote/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['freeFormNotes'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'freeFormNote'],
      });
      toast({
        title: 'Success',
        description: 'Free Form Note updated successfully',
        variant: 'default',
      });
      setIsFormOpen(false);
      setEditingFreeFormNote(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to update free form note';
      const errors = error.response?.data?.error?.details || {};

      setServerErrors(errors);

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Delete mutation (hard delete)
  const deleteFreeFormNoteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/api/master-data/freeFormNote/${id}?soft=false`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['freeFormNotes'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'freeFormNote'],
      });
      toast({
        title: 'Success',
        description: 'Free Form Note deleted successfully',
        variant: 'default',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message || 'Failed to delete free form note',
        variant: 'destructive',
      });
    },
  });

  // Table columns configuration
  const columns: TableColumn<FreeFormNote>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (_, freeFormNote) => (
        <div className="font-medium">{freeFormNote.name}</div>
      ),
    },
    {
      key: 'srNo',
      label: 'SR No',
      sortable: true,
      render: (_, freeFormNote) => (
        <div className="font-mono text-sm bg-muted px-2 py-1 rounded">
          {freeFormNote.srNo}
        </div>
      ),
    },
    {
      key: 'comment',
      label: 'Comment',
      sortable: false,
      render: (_, freeFormNote) => (
        <div className="text-sm text-muted-foreground max-w-xs truncate">
          {freeFormNote.comment || '-'}
        </div>
      ),
    },
    {
      key: 'maxShipmentValue',
      label: 'Max Shipment Value',
      sortable: true,
      render: (_, freeFormNote) => (
        <div className="text-sm font-medium">
          {freeFormNote.maxShipmentValue != null ? freeFormNote.maxShipmentValue.toLocaleString() : '-'}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_, freeFormNote) => (
        <Badge
          variant={
            freeFormNote.status === 'Active' ? 'default' : 'secondary'
          }
          className={
            freeFormNote.status === 'Active'
              ? 'bg-green-500 hover:bg-green-600'
              : ''
          }
        >
          {freeFormNote.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (_, freeFormNote) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(freeFormNote.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
  ];

  // Filter configuration
  const enhancedFilterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
      ],
      placeholder: 'Filter by status',
    },
  ];

  // Form sections configuration
  const formSections: FormSection[] = [
    {
      title: 'Free Form Note Information',
      description: 'Configure free form note details for shipments.',
      fields: [
        {
          name: 'name',
          label: 'Name',
          type: 'text',
          required: true,
          placeholder: 'Enter note name/description',
          description: 'A unique description for this note',
          validation: z
            .string()
            .min(2, 'Name must be at least 2 characters')
            .max(200, 'Name cannot exceed 200 characters'),
        },
        {
          name: 'srNo',
          label: 'SR No',
          type: 'text',
          required: true,
          placeholder: 'Enter serial number (e.g., "SR001", "NOTE-2024-001")',
          description: 'A unique serial number for this note',
          validation: z
            .string()
            .min(1, 'Serial number is required')
            .max(50, 'Serial number cannot exceed 50 characters'),
        },
        {
          name: 'comment',
          label: 'Comment',
          type: 'textarea',
          required: false,
          placeholder: 'Enter a short comment or description (optional)',
          description: 'Brief comment about this note',
          validation: z
            .string()
            .max(500, 'Comment cannot exceed 500 characters')
            .optional(),
        },
        {
          name: 'maxShipmentValue',
          label: 'Max Shipment Value',
          type: 'number',
          required: false,
          placeholder: 'Enter maximum shipment value (optional)',
          description: 'Maximum value of items to be shipped',
          validation: z
            .number()
            .min(0, 'Max shipment value cannot be negative')
            .optional()
            .or(z.literal('')),
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'Active', label: 'Active - Available for use' },
            { value: 'Inactive', label: 'Inactive - Not available for use' },
          ],
          description: 'Current status of this free form note',
        },
      ],
    },
  ];

  // Event handlers
  const handleCreate = () => {
    setEditingFreeFormNote(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (freeFormNote: FreeFormNote) => {
    setEditingFreeFormNote(freeFormNote);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = async (freeFormNote: FreeFormNote) => {
    if (
      window.confirm(
        `Are you sure you want to permanently delete "${freeFormNote.name}"? This action cannot be undone.`
      )
    ) {
      deleteFreeFormNoteMutation.mutate(freeFormNote._id);
    }
  };

  const handleSubmit = async (data: any) => {
    // Transform the data to uppercase SR No
    const transformedData = {
      ...data,
      srNo: data.srNo.toUpperCase(),
      maxShipmentValue: data.maxShipmentValue ? Number(data.maxShipmentValue) : undefined,
    };

    if (editingFreeFormNote) {
      updateFreeFormNoteMutation.mutate({
        id: editingFreeFormNote._id,
        data: transformedData,
      });
    } else {
      createFreeFormNoteMutation.mutate(transformedData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingFreeFormNote(null);
    setServerErrors({});
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'Free form notes data has been refreshed',
      variant: 'default',
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      // Add current filters to export
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(
        `/api/master-data/freeFormNote/export?${params.toString()}`
      );

      if (response.data.success) {
        const freeFormNotes = Array.isArray(response.data.data) ? response.data.data : [];

        if (freeFormNotes.length === 0) {
          toast({
            title: 'No records to export',
            description: 'There are no free form notes for the current filters.',
            variant: 'default',
          });
          return;
        }

        // Transform data for export
        const headers = [
          'Name',
          'SR No',
          'Comment',
          'Max Shipment Value',
          'Status',
          'Created At',
          'Updated At',
        ];
        const rows = freeFormNotes.map((note: FreeFormNote) => [
          note.name,
          note.srNo,
          note.comment || '',
          note.maxShipmentValue ?? '',
          note.status,
          new Date(note.createdAt).toLocaleString(),
          new Date(note.updatedAt).toLocaleString(),
        ]);
        const csvContent = buildCsv(headers, rows, { includeBom: true, quoteStrings: true });

        downloadText(
          csvContent,
          `free-form-notes-${new Date().toISOString().split('T')[0]}.csv`,
          'text/csv;charset=utf-8;'
        );

        toast({
          title: 'Success',
          description: `Exported ${freeFormNotes.length} free form notes`,
          variant: 'default',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Export Failed',
        description:
          error.response?.data?.error?.message || 'Failed to export free form notes',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>
            Free Form Note Management
          </h1>
          <p className='text-muted-foreground'>
            Manage free form notes with shipment values for logistics
          </p>
        </div>
      </div>

      <MasterDataTable<FreeFormNote>
        title='Free Form Notes'
        description='Manage and configure free form notes with shipment values'
        data={freeFormNotes}
        columns={columns}
        isLoading={isLoading}
        // Pagination
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Search
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder='Search free form notes by name, SR no, or comment...'
        // Filtering
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={enhancedFilterConfig}
        // Sorting
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        // Actions
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        // Permissions
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingFreeFormNote ? 'Edit Free Form Note' : 'Create Free Form Note'}
        sections={formSections}
        initialData={editingFreeFormNote}
        onSubmit={handleSubmit}
        isLoading={
          createFreeFormNoteMutation.isPending || updateFreeFormNoteMutation.isPending
        }
        mode={editingFreeFormNote ? 'edit' : 'create'}
        serverErrors={serverErrors}
      />
    </div>
  );
};

export default FreeFormNoteManagement;
